/**
 * Ce qu'une transaction d'argent dit d'elle-même : fonctions pures, sans I/O ni store.
 *
 * L'API Vault écrit sur chaque transaction une catégorie (une clé d'une liste fermée) et un sens
 * (créé, détruit, échangé). Cette table donne à chaque clé son nom français. Elle recopie la liste
 * de l'API (VaultApi/Trace/MoneyCategories.cs) : une clé que le panel ne connaît pas encore reste
 * affichée telle quelle, jamais masquée.
 *
 * Une transaction écrite avant ces champs n'a ni catégorie ni sens : elle se lit toujours, sous
 * « Origine inconnue ».
 */

export type MoneyFlow = 'creation' | 'destruction' | 'transfer' | 'unknown';

/** Le sens d'une catégorie. `by_sign` : créé quand le solde monte, détruit quand il baisse. */
export type CategoryFlow = MoneyFlow | 'by_sign';

export interface MoneyCategory {
  label: string;
  flow: CategoryFlow;
}

export const UNKNOWN_ORIGIN = 'Origine inconnue';

/** L'autre côté d'un mouvement que le jeu fait lui-même : argent créé ou détruit, sans joueur ni mod nommé. */
export const SERVER_ORIGIN = 'Serveur';

/** La liste fermée des catégories, dans l'ordre de l'API. */
export const MONEY_CATEGORIES: Record<string, MoneyCategory> = {
  // Argent créé
  'account.open': { label: 'Argent de départ', flow: 'creation' },
  'staff.give': { label: 'Don du staff', flow: 'creation' },
  'jobs.reward': { label: 'Récompense de métier', flow: 'creation' },
  'adminshop.sell': { label: "Vente à l'AdminShop", flow: 'creation' },
  'lootbox': { label: 'Lootbox', flow: 'creation' },
  'item.money': { label: 'Billet encaissé', flow: 'creation' },
  'reward.idle': { label: 'Récompense de présence', flow: 'creation' },
  'reward.warzone': { label: 'Récompense de Warzone', flow: 'creation' },
  'reward.bestiary': { label: 'Récompense du bestiaire', flow: 'creation' },
  'reward.quest': { label: 'Récompense de quête', flow: 'creation' },
  'reward': { label: 'Autre récompense', flow: 'creation' },
  'mail': { label: 'Argent reçu par courrier', flow: 'creation' },

  // Argent détruit
  'staff.take': { label: 'Retrait du staff', flow: 'destruction' },
  'adminshop.buy': { label: "Achat à l'AdminShop", flow: 'destruction' },
  'market.tax': { label: 'Taxe du marché', flow: 'destruction' },
  'market.tax.refund': { label: 'Taxe du marché rendue', flow: 'destruction' },
  'trade.tax': { label: "Taxe d'échange", flow: 'destruction' },
  'fee.command': { label: 'Commande payante', flow: 'destruction' },
  'fee.tool': { label: 'Outil payant', flow: 'destruction' },
  'fee.sign': { label: 'Panneau payant', flow: 'destruction' },
  'fee.anvil': { label: 'Enclume', flow: 'destruction' },
  'fee.travel': { label: "Billet d'avion", flow: 'destruction' },
  'fee': { label: 'Autres frais', flow: 'destruction' },
  'debt': { label: 'Remboursement de dette', flow: 'destruction' },

  // Créé ou détruit selon le signe
  'staff.set': { label: 'Solde fixé par le staff', flow: 'by_sign' },

  // Argent déplacé, ni créé ni détruit
  'player.pay': { label: 'Paiement entre joueurs', flow: 'transfer' },
  'player.pay.refund': { label: 'Paiement entre joueurs annulé', flow: 'transfer' },
  'trade': { label: 'Échange entre joueurs', flow: 'transfer' },
  'trade.refund': { label: 'Échange entre joueurs annulé', flow: 'transfer' },
  'market.buy': { label: 'Achat au marché', flow: 'transfer' },
  'market.sale': { label: 'Vente au marché', flow: 'transfer' },
  'market.escrow': { label: "Demande d'achat au marché", flow: 'transfer' },
  'market.refund': { label: 'Remboursement du marché', flow: 'transfer' },
  'bank.deposit': { label: 'Dépôt en banque', flow: 'transfer' },
  'bank.withdraw': { label: 'Retrait de banque', flow: 'transfer' },
  'bank.refund': { label: 'Dépôt en banque annulé', flow: 'transfer' },
};

/** Le nom français d'une catégorie ; sa clé telle quelle si le panel ne la connaît pas ; « Origine inconnue » sans clé. */
export function categoryLabel(key: string | null | undefined): string {
  const trimmed = key?.trim();
  if (!trimmed || trimmed === 'unknown') return UNKNOWN_ORIGIN;
  return MONEY_CATEGORIES[trimmed]?.label ?? trimmed;
}

/** Le nom français d'un sens. Une valeur absente ou imprévue vaut « Origine inconnue ». */
export function flowLabel(flow: string | null | undefined): string {
  switch (flow) {
    case 'creation': return 'Créé';
    case 'destruction': return 'Détruit';
    case 'transfer': return 'Échangé';
    default: return UNKNOWN_ORIGIN;
  }
}

/** Ce qu'il faut d'une transaction pour dire d'où elle vient et pourquoi. */
export interface MoneyTransactionLike {
  senderUuid?: string | null;
  senderName?: string | null;
  senderDisplayName?: string | null;
  reason?: string | null;
  source?: string | null;
  category?: string | null;
  flow?: string | null;
}

/**
 * Le détail par catégorie n'est montré que pour l'argent créé et pour l'argent détruit. Ce qui ne fait
 * que changer de mains (paiements, échanges, marché, banques) n'est pas détaillé à l'écran : un seul
 * mot dans l'historique d'un joueur, une seule ligne dans le tableau de la page Économie. L'API et le
 * satellite continuent d'enregistrer et de rendre la catégorie de chaque transaction.
 */
export const TRANSFER_MOTIF = 'Échangé';
export const TRANSFER_ROW_LABEL = 'Paiements, échanges, marché, banques';

/** Vrai pour une transaction d'échange : son sens le dit, sinon sa catégorie. */
function isTransfer(flow: string | null | undefined, category: string): boolean {
  const written = flow?.trim() ?? '';
  return written ? written === 'transfer' : MONEY_CATEGORIES[category]?.flow === 'transfer';
}

const UUID =/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Début d'une raison écrite par VaultMod à la place du mod (réglage ReasonFromSource). */
const AUTO_REASON = 'auto: ';

function isSystemName(name: string): boolean {
  return name.toLowerCase() === 'system';
}

/**
 * La colonne « Origine » : qui est de l'autre côté du mouvement. Un joueur (son pseudo, puis son
 * gamertag entre parenthèses s'ils diffèrent), sinon le nom que le mod s'est donné (« Banque France »,
 * « Market »), sinon le mod que VaultMod a reconnu, sinon « Serveur » pour de l'argent que la catégorie
 * dit créé ou détruit, sinon « Origine inconnue ». « System » n'est jamais affiché : il ne dit rien.
 */
export function transactionOrigin(transaction: MoneyTransactionLike): string {
  const senderName = transaction.senderName?.trim() ?? '';
  const pseudo = transaction.senderDisplayName?.trim() ?? '';
  const source = transaction.source?.trim() ?? '';

  if (transaction.senderUuid && UUID.test(transaction.senderUuid.trim())) {
    if (pseudo && senderName && pseudo.toLowerCase() !== senderName.toLowerCase()) return `${pseudo} (${senderName})`;
    return pseudo || senderName || 'Joueur inconnu';
  }
  if (senderName && !isSystemName(senderName)) return senderName;
  if (source) return source;
  // Récompense, vente à l'AdminShop, argent de départ, frais : la catégorie dit que le jeu a créé ou détruit
  // cet argent. Sans joueur ni mod nommé (VaultMod sans SendSource), l'autre côté est le serveur.
  if (isCreatedOrDestroyed(transaction.flow, transaction.category?.trim() ?? '')) return SERVER_ORIGIN;
  return UNKNOWN_ORIGIN;
}

/** Vrai pour de l'argent créé ou détruit : son sens le dit, sinon sa catégorie (un solde fixé compte aussi). */
function isCreatedOrDestroyed(flow: string | null | undefined, category: string): boolean {
  const written = flow?.trim() ?? '';
  if (written === 'creation' || written === 'destruction') return true;
  if (written) return false;
  const known = MONEY_CATEGORIES[category]?.flow;
  return known === 'creation' || known === 'destruction' || known === 'by_sign';
}

/**
 * La colonne « Motif » : pour de l'argent créé ou détruit, le nom de la catégorie suivi du détail donné
 * par le mod ; pour un échange (paiement, marché, banque), « Échangé » suivi de ce détail, sans nom de
 * catégorie. Sans catégorie, la raison telle que le mod l'a écrite ; sans rien, « Non précisé ».
 */
export function transactionMotif(transaction: MoneyTransactionLike): string {
  const reason = transaction.reason?.trim() ?? '';
  const category = transaction.category?.trim() ?? '';

  // Une raison « auto: » ne fait que répéter le mod et la méthode : elle n'explique rien de plus.
  const detail = reason.startsWith(AUTO_REASON) ? '' : reason;
  if (isTransfer(transaction.flow, category)) return detail ? `${TRANSFER_MOTIF} : ${detail}` : TRANSFER_MOTIF;
  if (category) {
    const label = categoryLabel(category);
    // Une raison d'aujourd'hui (« Trade », « Lootbox classique ») est déjà le nom de ce qu'elle décrit.
    return detail && detail.toLowerCase() !== label.toLowerCase() ? `${label} : ${detail}` : label;
  }
  if (detail) return detail;
  if (reason) return `Non précisé (appel ${reason.slice(AUTO_REASON.length)})`;
  return 'Non précisé';
}

/**
 * Les montants d'un jour par catégorie, sous leur nom français, pour le graphique en barres. Deux
 * clés de même nom s'additionnent.
 */
export function labelledAmounts(byCategory: Record<string, number> | null | undefined): Record<string, number> {
  const labelled: Record<string, number> = {};
  for (const [key, amount] of Object.entries(byCategory ?? {})) {
    const label = categoryLabel(key);
    labelled[label] = (labelled[label] ?? 0) + amount;
  }
  return labelled;
}

/** Un jour du graphique en barres empilées (la forme que lit StackedBarChart). */
export interface FlowBarDay {
  date: string;
  timestamp: number;
  total: number;
  reasons: Record<string, number>;
}

/** Ce qu'il faut d'un jour des mouvements d'argent pour en tirer ses barres. */
export interface FlowDayLike {
  date: string;
  timestamp: number;
  createdByCategory?: Record<string, number> | null;
  destroyedByCategory?: Record<string, number> | null;
  transferredByCategory?: Record<string, number> | null;
}

/**
 * Les barres d'un sens, jour par jour : une couleur par catégorie, sous son nom français. Une
 * catégorie dont le total du jour est nul ou négatif (plus rendu que pris) n'a pas de barre, et le
 * total du jour est la somme de ce qui est dessiné.
 */
export function flowBars(days: readonly FlowDayLike[] | null | undefined, flow: 'created' | 'destroyed' | 'transferred'): FlowBarDay[] {
  return (days ?? []).map((day) => {
    const byCategory = flow === 'created' ? day.createdByCategory : flow === 'destroyed' ? day.destroyedByCategory : day.transferredByCategory;
    const reasons: Record<string, number> = {};
    let total = 0;
    for (const [label, amount] of Object.entries(labelledAmounts(byCategory))) {
      if (amount > 0) {
        reasons[label] = amount;
        total += amount;
      }
    }
    return { date: day.date, timestamp: day.timestamp, total, reasons };
  });
}

/** Ce qu'il faut du total d'une catégorie sur la période pour en faire une ligne du tableau. */
export interface FlowCategoryLike {
  category: string | null;
  flow: string;
  in?: number | null;
  out?: number | null;
  count: number;
}

/** Une ligne du tableau de la période. */
export interface FlowTableRow {
  key: string;
  label: string;
  flow: string;
  in: number;
  out: number;
  count: number;
}

/**
 * Les lignes du tableau « Détail de la période » : une par catégorie pour l'argent créé et pour l'argent
 * détruit, UNE SEULE pour tous les échanges (leurs catégories s'additionnent), et ce qui n'est pas
 * classé tel quel. La ligne des échanges prend la place de la première catégorie d'échange reçue.
 */
export function flowTableRows(categories: readonly FlowCategoryLike[] | null | undefined): FlowTableRow[] {
  const rows: FlowTableRow[] = [];
  let transfers: FlowTableRow | null = null;
  for (const entry of categories ?? []) {
    const amounts = { in: entry.in ?? 0, out: entry.out ?? 0, count: entry.count };
    if (entry.flow === 'transfer') {
      if (!transfers) {
        transfers = { key: 'transfer', label: TRANSFER_ROW_LABEL, flow: 'transfer', in: 0, out: 0, count: 0 };
        rows.push(transfers);
      }
      transfers.in += amounts.in;
      transfers.out += amounts.out;
      transfers.count += amounts.count;
    } else {
      rows.push({ key: `${entry.flow}|${entry.category ?? ''}`, label: categoryLabel(entry.category), flow: entry.flow, ...amounts });
    }
  }
  return rows;
}

/**
 * Ce que la page dit d'une lecture des mouvements d'argent en erreur. 404 : le satellite du projet n'a pas
 * encore la route (version d'avant la 1.0.17). Sinon le message du satellite (base Vault injoignable).
 */
export function flowsError(status: number | undefined, error: string | undefined): string {
  if (status === 404) return 'le satellite de ce projet ne les sert pas encore, il faut le redéployer en version 1.0.17 ou plus.';
  if (status === 403) return "ton grade n'a pas accès aux statistiques de l'économie.";
  return error?.trim() || 'réponse vide';
}

/** Période la plus longue des mouvements d'argent, en jours entre le premier et le dernier : la règle du satellite. */
export const MAX_FLOW_DAYS = 366;

/** Le jour (UTC) d'une date AAAA-MM-JJ, ou null si ce n'en est pas une vraie. */
function dayOf(date: string | null | undefined): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date?.trim() ?? '');
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const time = Date.UTC(year, month - 1, day);
  const back = new Date(time);
  // 2026-02-31 glisserait au 3 mars : ce n'est pas une date.
  if (back.getUTCFullYear() !== year || back.getUTCMonth() !== month - 1 || back.getUTCDate() !== day) return null;
  return time / 86_400_000;
}

/**
 * Ce qui empêche de demander les mouvements d'argent entre deux jours (AAAA-MM-JJ, inclus), ou null si
 * la période est bonne. Les mêmes refus que le satellite (premier jour après le dernier, plus de
 * {@link MAX_FLOW_DAYS} jours), dits ici sans rien lui demander : une période saisie date après date
 * passe souvent par un état refusé, et la page doit garder son sélecteur pour en sortir.
 */
export function flowRangeError(startDate: string | null | undefined, endDate: string | null | undefined): string | null {
  const start = dayOf(startDate);
  const end = dayOf(endDate);
  if (start === null || end === null) return 'Choisis le premier et le dernier jour de la période.';
  if (start > end) return 'Le premier jour doit précéder le dernier.';
  if (end - start > MAX_FLOW_DAYS) return `La période ne peut pas dépasser ${MAX_FLOW_DAYS} jours.`;
  return null;
}
